import { createMotiSigApi, MotiSigApi } from '../api/motiSigApi';
import { generateInternalAnonymousId, resolveAnonymousId } from '../anonymous/anonymousId';
import { MotiSigApiError, MotiSigError } from '../errors';
import { AsyncQueue } from '../internal/asyncQueue';
import { logger, setLogLevel } from '../internal/logger';
import {
  STORAGE_KEYS,
  storageGetItem,
  storageRemoveItem,
  storageSetItem,
} from '../internal/storageAdapter';
import { resolveClientBaseUrl } from '../runtime/resolveClientBaseUrl';
import {
  getActivePushSubscription,
  getPushPermission,
  isPushSupported,
  requestPushPermission,
  subscribePush as subscribeBrowserPush,
  type SerializedPushSubscription,
} from '../push/pushManager';
import { syncPushServiceWorker } from '../push/serviceWorkerSync';
import { IDB_KEYS, idbRemove, idbSet, type MotiSigIdbCreds } from '../internal/idbStorage';
import type {
  AnonymousIdProvider,
  ActivityBatchEvent,
  ActivityTrackingOptions,
  ClientContextInput,
  ClientDeviceInfo,
  ClientLocation,
  EventIdentity,
  MotiSigIdentity,
  MotiSigWebOptions,
  RegisterUserPayload,
  SubscribePushResult,
  UpdateUserPayload,
  WebPushOptions,
} from '../types';

const DEFAULT_ACTIVITY_FLUSH_MS = 30_000;
const LOCATION_CHANGE_EVENT = 'motisig:locationchange';

function resolveIntl(): { timezone?: string; locale?: string } {
  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    return { timezone: opts.timeZone, locale: opts.locale };
  } catch {
    return {};
  }
}

function resolveBrowser(userAgent: string): string | undefined {
  if (!userAgent) return undefined;
  if (userAgent.includes('Edg/')) return 'Edge';
  if (userAgent.includes('OPR/') || userAgent.includes('Opera')) return 'Opera';
  if (userAgent.includes('Firefox/')) return 'Firefox';
  if (userAgent.includes('Chrome/')) return 'Chrome';
  if (userAgent.includes('Safari/')) return 'Safari';
  return undefined;
}

function resolveOs(userAgent: string): string | undefined {
  if (!userAgent) return undefined;
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac OS X')) return 'macOS';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
  if (userAgent.includes('Linux')) return 'Linux';
  return undefined;
}

function resolveDeviceContext(): ClientDeviceInfo {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
  const screenWidth =
    typeof window !== 'undefined' && window.screen ? window.screen.width : undefined;
  const screenHeight =
    typeof window !== 'undefined' && window.screen ? window.screen.height : undefined;
  return {
    platform: 'web',
    userAgent,
    browser: userAgent ? resolveBrowser(userAgent) : undefined,
    os: userAgent ? resolveOs(userAgent) : undefined,
    screenWidth,
    screenHeight,
  };
}

async function resolveLocation(enabled: boolean): Promise<ClientLocation | undefined> {
  if (!enabled || typeof navigator === 'undefined' || !navigator.geolocation) return undefined;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: position.coords.accuracy,
        }),
      () => resolve(undefined),
      {enableHighAccuracy: false, timeout: 2500, maximumAge: 60000}
    );
  });
}

function mergeClientContext(
  base: ClientContextInput,
  override?: ClientContextInput
): ClientContextInput {
  return {
    ...base,
    ...override,
    location: override?.location ?? base.location,
    device: {
      ...(base.device ?? {}),
      ...(override?.device ?? {}),
    },
  };
}

export class MotiSigWeb {
  private api: MotiSigApi | null = null;
  private queue = new AsyncQueue();
  private initialized = false;

  private _currentUserId: string | null = null;
  private _anonymousId: string | null = null;
  private _anonymousIdProvider: AnonymousIdProvider | null = null;
  private includeGeolocation = false;
  private anonymousUserExtras: Omit<
    RegisterUserPayload,
    'id' | 'platform' | 'anonymousId' | 'anonymousIdProvider'
  > | null = null;
  private activityTransportEnabled = false;
  private activityTrackingEnabled = false;
  private autoPageViewEnabled = false;
  private activityBuffer: Array<{
    eventName: string;
    eventData?: Record<string, unknown>;
    occurredAt?: string;
  }> = [];
  private activityFlushTimer: ReturnType<typeof setInterval> | null = null;
  private activityFlushIntervalMs = DEFAULT_ACTIVITY_FLUSH_MS;
  private sessionId = typeof crypto !== 'undefined' && crypto.randomUUID ?
    crypto.randomUUID()
  : `sess_${Date.now()}`;
  private activityListenersAttached = false;
  private autoPageViewListenersAttached = false;
  private onPageHide: (() => void) | null = null;
  private onVisibilityChange: (() => void) | null = null;
  private onLocationChange: (() => void) | null = null;
  private lastTrackedHref: string | null = null;
  private pushOptions: WebPushOptions | null = null;
  private vapidPublicKey: string | null = null;
  private pushRegistration: ServiceWorkerRegistration | null = null;
  private pushSubscription: SerializedPushSubscription | null = null;
  private sdkKey = '';
  private projectId = '';
  private baseURL = '';

  get isInitialized(): boolean {
    return this.initialized;
  }

  get currentUserId(): string | null {
    return this._currentUserId;
  }

  getAnonymousId(): string | null {
    return this._anonymousId;
  }

  getAnonymousIdProvider(): AnonymousIdProvider | null {
    return this._anonymousIdProvider;
  }

  async initialize(options: MotiSigWebOptions): Promise<boolean> {
    setLogLevel(options.logLevel ?? 'info');
    if (this.initialized) return true;

    const sdkKey = options.sdkKey.trim();
    const projectId = options.projectId.trim();
    if (!sdkKey || !projectId) {
      logger.warn('initialize: missing sdkKey or projectId');
      return false;
    }

    const baseUrl = resolveClientBaseUrl(options.baseURL);
    this.api = createMotiSigApi({ baseUrl, sdkKey, projectId });
    this.initialized = true;
    this.sdkKey = sdkKey;
    this.projectId = projectId;
    this.baseURL = baseUrl;
    this.pushOptions = options.push ?? null;
    this.includeGeolocation = options.anonymous?.geolocation === true;
    this.anonymousUserExtras = options.anonymousUserExtras ?? null;

    const creds: MotiSigIdbCreds = { sdkKey, projectId, baseURL: baseUrl };
    void idbSet(IDB_KEYS.creds, creds).catch((e) => logger.warn('idb creds write failed', e));

    const anonymousEnabled = options.anonymous?.enabled !== false;
    if (anonymousEnabled) {
      try {
        const resolved = await resolveAnonymousId(options.anonymous);
        this._anonymousId = resolved.anonymousId;
        this._anonymousIdProvider = resolved.provider;

        const persistedUserId = storageGetItem(STORAGE_KEYS.userId);
        this._currentUserId = persistedUserId;
      } catch (e) {
        logger.warn('anonymous initialization failed', e);
      }
    } else {
      this._currentUserId = storageGetItem(STORAGE_KEYS.userId);
    }

    if (this.pushOptions?.enabled === true) {
      void this.initPushIfEnabled();
    }

    return true;
  }

  getPushPermission(): NotificationPermission {
    return getPushPermission();
  }

  async subscribePush(): Promise<SubscribePushResult> {
    this.ensureReady();
    if (this.pushOptions?.enabled !== true) {
      return { ok: false, reason: 'not_enabled' };
    }
    if (!isPushSupported()) return { ok: false, reason: 'unsupported' };

    const permission = await requestPushPermission();
    if (permission !== 'granted') return { ok: false, reason: 'denied' };

    if (!this._currentUserId && !this._anonymousId) {
      return { ok: false, reason: 'no_user' };
    }

    const vapidKey = await this.ensureVapidPublicKey();
    if (!vapidKey) return { ok: false, reason: 'unsupported' };

    const registration = await this.syncPushServiceWorkerRegistration();
    if (!registration) return { ok: false, reason: 'unsupported' };
    this.pushRegistration = registration;

    const sub = await subscribeBrowserPush(registration, vapidKey);
    this.pushSubscription = sub;
    const token = JSON.stringify(sub);
    storageSetItem(STORAGE_KEYS.pushSubscribed, '1');
    storageSetItem(STORAGE_KEYS.pushToken, token);

    await this.queue.run(async () => {
      const identity = this.resolveIdentity();
      await this.registerAndEnableWebPush(identity, token);
    });

    return { ok: true };
  }

  async unsubscribePush(): Promise<void> {
    this.ensureReady();
    const token = this.pushSubscription ? JSON.stringify(this.pushSubscription) : storageGetItem(STORAGE_KEYS.pushToken);
    if (this.pushRegistration) {
      const active = await getActivePushSubscription(this.pushRegistration);
      await active?.unsubscribe();
    }
    if (token && (this._currentUserId || this._anonymousId)) {
      await this.queue.run(async () => {
        const identity = this.resolveIdentity();
        await this.api!.removePushSubscription(identity, {
          devicePlatform: 'web',
          pushType: 'webpush',
          token,
        });
      });
    }
    this.pushSubscription = null;
    storageRemoveItem(STORAGE_KEYS.pushSubscribed);
    storageRemoveItem(STORAGE_KEYS.pushToken);
  }

  async setPushEnabled(enabled: boolean): Promise<void> {
    this.ensureReady();
    const identity = this.resolveIdentity();
    const token = this.pushSubscription ? JSON.stringify(this.pushSubscription) : storageGetItem(STORAGE_KEYS.pushToken);
    if (!token) throw new MotiSigError('no push subscription', 'no_user');
    await this.queue.run(async () => {
      await this.api!.patchPushSubscription(identity, {
        devicePlatform: 'web',
        pushType: 'webpush',
        token,
        enabled,
      });
    });
  }

  private async initPushIfEnabled(): Promise<void> {
    try {
      await this.ensureVapidPublicKey();
      const registration = await this.syncPushServiceWorkerRegistration();
      if (registration) this.pushRegistration = registration;
      if (this.pushOptions?.autoSubscribe && getPushPermission() === 'granted') {
        await this.subscribePush();
      }
    } catch (e) {
      logger.warn('push init failed', e);
    }
  }

  private async syncPushServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (this.pushOptions?.enabled !== true) return null;
    const swPath = this.pushOptions.serviceWorkerPath ?? '/motisig-sw.js';
    const swScope = this.pushOptions.serviceWorkerScope ?? '/';
    return syncPushServiceWorker({
      swPath,
      swScope,
      cdnBaseUrl: this.pushOptions.cdnBaseUrl,
    });
  }

  private async ensureVapidPublicKey(): Promise<string | null> {
    if (this.vapidPublicKey) return this.vapidPublicKey;
    if (this.pushOptions?.vapidPublicKey) {
      this.vapidPublicKey = this.pushOptions.vapidPublicKey;
      await idbSet(IDB_KEYS.vapidPublicKey, this.vapidPublicKey).catch(() => undefined);
      return this.vapidPublicKey;
    }
    const config = await this.api!.getWebConfig();
    this.vapidPublicKey = config.vapidPublicKey;
    if (this.vapidPublicKey) {
      await idbSet(IDB_KEYS.vapidPublicKey, this.vapidPublicKey).catch(() => undefined);
    }
    return this.vapidPublicKey;
  }

  private async registerAndEnableWebPush(identity: EventIdentity, token: string): Promise<void> {
    await this.api!.upsertPushSubscription(identity, {
      devicePlatform: 'web',
      pushType: 'webpush',
      token,
      permission: 'granted',
      enabled: false,
    });
    await this.api!.patchPushSubscription(identity, {
      devicePlatform: 'web',
      pushType: 'webpush',
      token,
      enabled: true,
    });
  }

  async identify(userId: string, extras?: Omit<RegisterUserPayload, 'id'>): Promise<void> {
    this.ensureReady();
    const externalId = userId.trim();
    if (!externalId) throw new MotiSigError('identify requires a non-empty userId', 'no_user');

    const priorPathKey = this._currentUserId ?? this._anonymousId;
    const hadPush = !!(this.pushSubscription || storageGetItem(STORAGE_KEYS.pushToken));

    await this.queue.run(async () => {
      const result = await this.api!.identify(externalId, {
        anonymousId: this._anonymousId ?? undefined,
        anonymousIdProvider: this._anonymousIdProvider ?? undefined,
        ...(this.anonymousUserExtras ?? {}),
        ...extras,
      });

      if (result.anonymousId && result.anonymousId !== this._anonymousId) {
        this._anonymousId = result.anonymousId;
        storageSetItem(STORAGE_KEYS.anonymousId, result.anonymousId);
      }

      this._currentUserId = result.userId;
      storageSetItem(STORAGE_KEYS.userId, result.userId);

      if (hadPush && priorPathKey && priorPathKey !== result.userId) {
        const token =
          this.pushSubscription ?
            JSON.stringify(this.pushSubscription)
          : storageGetItem(STORAGE_KEYS.pushToken);
        if (token) {
          try {
            const priorIdentity: EventIdentity =
              this._currentUserId ?
                { userId: this._currentUserId }
              : {
                  anonymousId: this._anonymousId!,
                  anonymousIdProvider: this._anonymousIdProvider ?? undefined,
                };
            await this.api!.removePushSubscription(priorIdentity, {
              devicePlatform: 'web',
              pushType: 'webpush',
              token,
            });
          } catch {
            // orphan anonymous subscription is acceptable
          }
          await this.registerAndEnableWebPush({ userId: result.userId }, token);
        }
      }
    });
  }

  async updateUser(payload: UpdateUserPayload): Promise<void> {
    await this.withUser((uid) => this.api!.updateUser(uid, payload));
  }

  async addTags(tags: string[]): Promise<void> {
    this.ensureReady();
    const identity = this.resolveIdentity();
    await this.queue.run(() => this.api!.addTags(identity, tags));
  }

  async removeTags(tags: string[]): Promise<void> {
    this.ensureReady();
    const identity = this.resolveIdentity();
    await this.queue.run(() => this.api!.removeTags(identity, tags));
  }

  async addOrUpdateAttributes(attributes: Record<string, unknown>): Promise<void> {
    this.ensureReady();
    const identity = this.resolveIdentity();
    await this.queue.run(() => this.api!.addOrUpdateAttributes(identity, attributes));
  }

  async removeAttributes(keys: string[]): Promise<void> {
    this.ensureReady();
    const identity = this.resolveIdentity();
    await this.queue.run(() => this.api!.removeAttributes(identity, keys));
  }

  async event(eventName: string, eventData?: Record<string, unknown>): Promise<string> {
    this.ensureReady();
    const name = eventName.trim();
    const uid = this._currentUserId;
    const identity: EventIdentity =
      uid ?
        { userId: uid }
      : this._anonymousId ?
        { anonymousId: this._anonymousId, anonymousIdProvider: this._anonymousIdProvider ?? undefined }
      : {};
    if (!identity.userId && !identity.anonymousId) {
      throw new MotiSigError('no current user', 'no_user');
    }
    return this.queue.run(async () => {
      const context = await this.collectClientContext();
      return this.api!.event(identity, name, eventData, context);
    });
  }

  getIdentity(): MotiSigIdentity {
    return {
      userId: this._currentUserId,
      anonymousId: this._anonymousId,
      anonymousIdProvider: this._anonymousIdProvider,
    };
  }

  enableAutoPageView(): void {
    this.ensureReady();
    if (this.autoPageViewEnabled || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    this.autoPageViewEnabled = true;
    this.ensureActivityTransport();

    if (!this.autoPageViewListenersAttached) {
      this.autoPageViewListenersAttached = true;
      const wrap = (type: 'pushState' | 'replaceState') => {
        const orig = history[type];
        history[type] = function (this: History, ...args: unknown[]) {
          const ret = orig.apply(this, args as never);
          window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
          return ret;
        } as History[typeof type];
      };
      wrap('pushState');
      wrap('replaceState');
      window.addEventListener('popstate', () => window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT)));

      this.onLocationChange = () => {
        if (location.href !== this.lastTrackedHref) {
          void this.trackCurrentPageView();
        }
      };
      window.addEventListener(LOCATION_CHANGE_EVENT, this.onLocationChange);
    }

    void this.trackCurrentPageView();
  }

  enableActivityTracking(options: ActivityTrackingOptions = {}): void {
    this.ensureReady();
    this.ensureActivityTransport(options.flushIntervalMs);

    if (this.activityTrackingEnabled) return;
    this.activityTrackingEnabled = true;

    if (!this.activityListenersAttached && typeof document !== 'undefined') {
      this.activityListenersAttached = true;
      if (options.appOpens !== false) {
        void this.trackEngagementEvent('session_start');
        void this.trackEngagementEvent('app_open');
      }
      this.onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          void this.trackEngagementEvent('app_foreground');
        } else {
          void this.trackEngagementEvent('app_background');
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  logout(): void {
    const wasRegistered = !!this._currentUserId;
    void this.unsubscribePush().catch(() => undefined);
    this._currentUserId = null;
    storageRemoveItem(STORAGE_KEYS.userId);
    if (wasRegistered) {
      const rotated = generateInternalAnonymousId();
      this._anonymousId = rotated;
      this._anonymousIdProvider = 'internal';
      storageSetItem(STORAGE_KEYS.anonymousId, rotated);
      storageSetItem(STORAGE_KEYS.anonymousIdProvider, 'internal');
    }
  }

  reset(): void {
    this.teardownActivityTransport();
    this.activityTrackingEnabled = false;
    this.autoPageViewEnabled = false;
    this.activityBuffer = [];
    void this.unsubscribePush().catch(() => undefined);
    this.pushOptions = null;
    this.vapidPublicKey = null;
    this.pushRegistration = null;
    this._currentUserId = null;
    this._anonymousId = null;
    this._anonymousIdProvider = null;
    storageRemoveItem(STORAGE_KEYS.userId);
    storageRemoveItem(STORAGE_KEYS.anonymousId);
    storageRemoveItem(STORAGE_KEYS.anonymousIdProvider);
    storageRemoveItem(STORAGE_KEYS.pushSubscribed);
    storageRemoveItem(STORAGE_KEYS.pushToken);
    void idbRemove(IDB_KEYS.creds).catch(() => undefined);
    void idbRemove(IDB_KEYS.vapidPublicKey).catch(() => undefined);
  }

  private ensureActivityTransport(flushIntervalMs?: number): void {
    if (flushIntervalMs !== undefined) {
      this.activityFlushIntervalMs = flushIntervalMs;
    }
    if (this.activityTransportEnabled) return;
    this.activityTransportEnabled = true;

    this.activityFlushTimer = setInterval(() => {
      void this.flushActivityBatch();
    }, this.activityFlushIntervalMs);

    if (typeof window !== 'undefined') {
      this.onPageHide = () => {
        void this.flushActivityBatch();
      };
      window.addEventListener('pagehide', this.onPageHide);
    }
  }

  private teardownActivityTransport(): void {
    if (this.activityFlushTimer) {
      clearInterval(this.activityFlushTimer);
      this.activityFlushTimer = null;
    }
    this.activityTransportEnabled = false;

    if (typeof window !== 'undefined' && this.onPageHide) {
      window.removeEventListener('pagehide', this.onPageHide);
      this.onPageHide = null;
    }
    if (typeof document !== 'undefined' && this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.onVisibilityChange = null;
    }
    if (typeof window !== 'undefined' && this.onLocationChange) {
      window.removeEventListener(LOCATION_CHANGE_EVENT, this.onLocationChange);
      this.onLocationChange = null;
    }
    this.activityListenersAttached = false;
    this.autoPageViewListenersAttached = false;
    this.lastTrackedHref = null;
  }

  private async trackCurrentPageView(): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    this.lastTrackedHref = location.href;
    await this.trackEngagementEvent('page.viewed', {
      path: location.pathname + location.search,
      url: location.href,
      referrer: document.referrer || undefined,
      title: document.title || undefined,
    });
  }

  private async trackEngagementEvent(
    eventName: string,
    eventData?: Record<string, unknown>,
  ): Promise<void> {
    if (!this.activityTransportEnabled) return;

    this.activityBuffer.push({
      eventName,
      eventData,
      occurredAt: new Date().toISOString(),
    });

    if (eventName === 'page.viewed' || this.activityBuffer.length >= 20) {
      await this.flushActivityBatch();
    }
  }

  private async flushActivityBatch(): Promise<void> {
    if (!this.api || this.activityBuffer.length === 0) return;
    const uid = this._currentUserId;
    const batch = this.activityBuffer.splice(0, this.activityBuffer.length);
    const context = await this.collectClientContext();
    const events: ActivityBatchEvent[] = [];
    for (const evt of batch) {
      const base: ActivityBatchEvent = {
        eventName: evt.eventName,
        occurredAt: evt.occurredAt,
        context,
        platform: 'web',
        sessionId: this.sessionId,
        eventData: evt.eventData,
      };
      if (uid) {
        events.push({ ...base, userId: uid });
      } else if (this._anonymousId) {
        events.push({
          ...base,
          anonymousId: this._anonymousId,
          anonymousIdProvider: this._anonymousIdProvider ?? undefined,
        });
      }
    }

    if (events.length === 0) return;
    try {
      await this.api.activityBatch(events);
    } catch (e) {
      logger.warn('activity batch flush failed', e);
      this.activityBuffer.unshift(...batch);
    }
  }

  private async collectClientContext(): Promise<ClientContextInput> {
    const intl = resolveIntl();
    const context: ClientContextInput = {
      locale: intl.locale,
      timezone: intl.timezone,
      device: resolveDeviceContext(),
    };
    const location = await resolveLocation(this.includeGeolocation);
    if (location) {
      context.location = location;
    }
    return context;
  }

  private ensureReady(): void {
    if (!this.initialized || !this.api) {
      throw new MotiSigError('MotiSig web SDK not initialized', 'not_initialized');
    }
  }

  private resolveIdentity(): EventIdentity {
    const uid = this._currentUserId;
    const identity: EventIdentity =
      uid ?
        { userId: uid }
      : this._anonymousId ?
        { anonymousId: this._anonymousId, anonymousIdProvider: this._anonymousIdProvider ?? undefined }
      : {};
    if (!identity.userId && !identity.anonymousId) {
      throw new MotiSigError('no current user', 'no_user');
    }
    return identity;
  }

  private async withUser<T>(fn: (userId: string) => Promise<T>): Promise<T> {
    this.ensureReady();
    const uid = this._currentUserId;
    if (!uid) throw new MotiSigError('no current user', 'no_user');
    return this.queue.run(() => fn(uid));
  }
}
